// Home.js — merged from home.txt (new) + Home.js (live)
//
// - Keeps Helmet from react-helmet-async (live) ✅
// - Preserves/enhances SEO: canonical, meta, plus JSON-LD (new) ✅
// - Uses new sections: HeroCyber, ServicesWaterfall, FAQ, NewWhoWeAre ✅

import React from "react";
import Footer from "./Footer";
import Hero from "../ui/HeroCyber.tsx";                 // new hero
import ServicesCascade from "../ui/ServicesWaterfall.tsx"; // new services layout
import FAQ from "../ui/FAQ.tsx";                        // new FAQ component
import WhoWeAre from "../ui/NewWhoWeAre.tsx";           // new team/solo section
import {
  Box,
  Heading,
  Text,
  VStack,
  Container,
  Link,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async"; // ✅ keep async version

const Home = () => {
  // --- SEO config (from new file, but we keep live title/desc defaults) ---
  const site = {
    name: "Gemini Cyber Security",
    domain: "https://gemini-cybersecurity.com",
    logo: "https://gemini-cybersecurity.com/og-logo.png",     // update if you have a better asset
    ogImage: "https://gemini-cybersecurity.com/og-image.jpg", // update if you have a better asset
    email: "rowan@gemini-cybersecurity.com",
    phone: "", // optional
    city: "Johannesburg",
    country: "South Africa",
    socials: ["https://www.linkedin.com/company/gemini-cybersecurity"],
  };

  // JSON-LD blocks (from new home.txt)
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: site.name,
    url: site.domain,
    logo: site.logo,
    sameAs: site.socials.filter(Boolean),
    contactPoint: site.phone
      ? [
          {
            "@type": "ContactPoint",
            contactType: "sales",
            email: site.email,
            telephone: site.phone,
          },
        ]
      : [{ "@type": "ContactPoint", contactType: "sales", email: site.email }],
  };

  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Your Name",
    jobTitle: "Founder & Security Engineer",
    worksFor: { "@type": "Organization", name: site.name, url: site.domain },
    email: "mailto:rowan@gemini-cybersecurity.com",
    image: `${site.domain}/images/founder.jpg`, // update
    sameAs: ["https://www.linkedin.com/company/gemini-cybersecurity"],
  };

  const servicesJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: [
      {
        "@type": "Service",
        name: "Penetration Testing",
        serviceType: "Offensive Security",
        areaServed: site.country,
        provider: { "@type": "Organization", name: site.name },
        offers: {
          "@type": "Offer",
          url: `${site.domain}/#services`,
          priceCurrency: "USD",
        },
      },
      {
        "@type": "Service",
        name: "Red Teaming",
        serviceType: "Adversary Simulation",
        areaServed: site.country,
        provider: { "@type": "Organization", name: site.name },
      },
      {
        "@type": "Service",
        name: "ISO 27001 / 27701 Consulting",
        serviceType: "GRC",
        areaServed: site.country,
        provider: { "@type": "Organization", name: site.name },
      },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How fast can you start a penetration test?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We can usually begin scoping within 24 hours and start testing within 5 business days, depending on complexity.",
        },
      },
      {
        "@type": "Question",
        name: "Do you include a remediation re-test?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. A complimentary re-test within 30 days verifies fixes and updates your report for stakeholders.",
        },
      },
      {
        "@type": "Question",
        name: "Can you help us get ISO 27001 certified?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We run gap assessments, policy development, internal audits, and audit readiness to support ISO 27001 certification.",
        },
      },
    ],
  };

  // Keep live title/description (so you don’t regress existing SEO) and
  // still align with broader services positioning.
  const title =
    "Gemini Cybersecurity | Compliance Updates Made Simple"; // from live
  const description =
    "Stay ahead of ISO and compliance changes. Receive plain-language updates and actions directly in your inbox."; // from live

  return (
    <>
      <Helmet>
        {/* Core SEO (live) */}
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={site.domain} />
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />

        {/* Open Graph / Twitter */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={site.name} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={site.domain} />
        <meta property="og:image" content={site.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={site.ogImage} />

        {/* JSON-LD (new) */}
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(servicesJsonLd)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(personJsonLd)}
        </script>
      </Helmet>

      <Box bg="black" color="white">
        {/* Top Nav */}
        <div className="relative w-screen bg-slate-950 text-white">
          <nav className="fixed top-0 left-0 w-full bg-slate-950 py-4 text-center z-50">
            <ul className="flex justify-center space-x-8">
              <li>
                <a href="/" className="text-white hover:text-blue-400 text-lg">
                  Home
                </a>
              </li>
              <li>
                <a
                  href="#about-us"
                  className="text-white hover:text-blue-400 text-lg"
                >
                  About Us
                </a>
              </li>
              <li>
                <a
                  href="#contact-us"
                  className="text-white hover:text-blue-400 text-lg"
                >
                  Contact Us
                </a>
              </li>
              <li>
                <a
                  href="#services"
                  className="text-white hover:text-blue-400 text-lg"
                >
                  Services
                </a>
              </li>
            </ul>
          </nav>
        </div>

        {/* Hero (new) */}
        <Hero />

        {/* About Us (kept text) */}
        <Box py={20} bg="black" color="white" id="about-us">
          <Container maxW="container.lg">
            <VStack spacing={8} align="stretch">
              <Heading as="h2" size="xl" textAlign="center">
                About Us
              </Heading>
              <Text fontSize="lg">
                At Gemini Cyber Security we specialise in offensive security
                services, such as penetration testing and cybersecurity compliance
                consulting. Our certified security experts perform manual and
                automated penetration tests to uncover vulnerabilities across
                networks, applications, and cloud environments. We help businesses
                of all sizes strengthen their defenses against cyberattacks by
                providing clear, actionable reports and remediation guidance.
                <br />
                <br />
                In addition to offensive security testing, Gemini Cyber Security
                offers full-service cybersecurity compliance support. We assist
                organisations with meeting major regulatory requirements, including
                GDPR compliance, HIPAA compliance, PCI-DSS compliance, SOC 2
                readiness, and ISO 27001 certification. Our compliance consulting
                services are designed to help organisations before, during and
                after the audit process, as well as build trust with customers.
              </Text>
              <Text fontSize="lg">
                To make ongoing compliance easier, we are developing an AI-driven
                compliance solution that will update you on the standards you need
                and any regulations you request. Our AI tool will provide
                business with actionable insights, helping you to stay compliant
                without constant manual research.
                <br />
                <br />
                Partner with Gemini Cyber Security for expert penetration testing,
                proactive cybersecurity consulting, and AI-powered compliance
                monitoring, all designed to help your business and simplify
                regulatory management.
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* Who we are (new section/component) */}
        <WhoWeAre mode="team" /> {/* or mode="solo" */}

        {/* Services (new cascading layout) */}
        <div id="services">
          <ServicesCascade />
        </div>

        {/* FAQ (new component, pairs with FAQPage JSON-LD) */}
        <FAQ />

        {/* Contact */}
        <Box py={10} bg="black" color="white" id="contact-us">
          <Container maxW="container.lg">
            <VStack spacing={8} align="center">
              <Heading as="h2" size="xl" textAlign="center">
                Contact Us
              </Heading>
              <Text fontSize="lg" textAlign="center">
                Don’t wait until after a breach, secure your business today.
              </Text>

              <VStack spacing={3} align="center">
                <Text fontSize="md">
                  Email:{" "}
                  <Link
                    href="mailto:rowan@gemini-cybersecurity.com"
                    color="cyan.400"
                  >
                    rowan@gemini-cybersecurity.com
                  </Link>
                </Text>
                <Text fontSize="md">or</Text>
                <a
                  href="https://calendly.com/rowan-gemini-cybersecurity/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="relative px-10 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg">
                    Book Appointment
                  </button>
                </a>
              </VStack>
            </VStack>
          </Container>
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default Home;
